import { OutlineButton } from "components/button";
import { IStatusCount, IFilter } from "../interface";
import { useTranslation } from 'react-i18next';
import Dropdown from "components/dropdown";
import { IoChevronBackOutline, IoClose } from "react-icons/io5";
import { FaPlus, FaInfoCircle } from "react-icons/fa";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement";

interface Props {
  counts: IStatusCount[] | undefined; 
  setTotalPages: (a: number) => void;
  showSidebar: boolean;
  setShowSidebar: (a: boolean) => void;
  filters: IFilter[];
  setFilters: (a: IFilter[]) => void;
  selected: string;
  setSelected: (a: string) => void;
  exactMatch: boolean;
  setExactMatch: (a: boolean) => void;
  isExpiring: boolean;
  setIsExpiring: (a: boolean) => void;
}

export default function Sidebar({
  showSidebar,
  setShowSidebar,
  counts,
  filters,
  setFilters,
  selected,
  setSelected,
  exactMatch,
  setExactMatch,
  isExpiring,
  setIsExpiring,
}: Props) {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.Sidebar";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const filterOptions = [
    { label: tr("Plan Name"), value: "plan_name" },
    { label: tr("City"), value: "city" },
    { label: tr("State"), value: "state" },
    { label: tr("Zip"), value: "zip" },
    { label: tr("Duration"), value: "duration" },
    { label: tr("Tags"), value: "tags" },
    { label: tr("Start Date Range"), value: "date_range" },
  ];

  const statuses = [
    "Approved", 
    "Pending", 
    "Expired", 
    "Declined",
    "Failed",
  ];

  const expiringCount = counts?.find((item) => item.label === "Expiring Soon")?.count || 0;

  return (
    <div className="border-r flex flex-col z-10">
      {showSidebar && (
        <button 
          className="absolute h-6 w-6 p-0.5 z-10 top-[5px] right-[-14px] text-primary border-2 border-primary bg-white rounded-full hover:bg-primary hover:text-white" 
          onClick={() => setShowSidebar(false)}
        >
          <IoChevronBackOutline size={18} className="-ml-[1px]" />
        </button>
      )}

      <OutlineButton
        onClick={() => setIsExpiring(!isExpiring)}
        className={`${
          isExpiring ? "!bg-red-600 !text-white" : "!bg-red-100 !text-red-600"
        }  !border-red-600 !my-2 !p-2 !w-[90%] !flex !self-center !justify-between`}
      >
        <span>{tr("Expiring Soon")}</span>
        <span className="bg-white px-2 rounded-full text-red-600">
          {expiringCount}
        </span>
      </OutlineButton>
      
      <div className="p-0 mx-4 mt-4 flex items-center justify-between">
        <p>
          {tr("Status")}
        </p>
        <button
          onClick={() => setSelected("")} 
          className="text-primary"
        >
          {tr("Show All")}
        </button>
      </div>
      {statuses?.map((label, i) => {
        const match = counts?.find((status) => status.label === label);

        return (
          <button
            className={`
              flex items-center justify-between text-sm cursor-pointer px-4 py-3
              ${label === selected ? "bg-slate-200 text-primary" : "text-gray-700 hover:bg-slate-100"}
            `}
            key={i}
            onClick={() => {
              if (label === selected) {
                setSelected("");
              } else {
                setSelected(label);
              }
            }}
          >
                        {/* <div className="flex items-center space-x-2">
              <span>{tr(label)}</span>
              {isExpiring && match && (
                <span className="text-xs text-red-500 bg-red-100 px-1 rounded">
                  expiring
                </span>
              )}
                            <span>{tr(label)}</span>
            </div> */}
            <span>{tr(label)}</span>
            <div>{match?.count || 0}</div>
          </button>
        );
      })}


      <div className="mt-4 mb-2 pb-2 px-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div>
            {tr("Filters")}
          </div>
          <Dropdown
            reference={
              <button className="bg-white border-2 border-primary text-primary rounded-full p-0.5 hover:bg-primary hover:text-white">
                <FaPlus />
              </button>
            }
              data={filterOptions.map((option) => ({
                button: [
                  {
                    label: option.label,
                    onClick: () => {
                      const newFilters = [
                        ...filters,
                        {
                          id: filters.length + 1,
                          label: option.label,
                          value: option.value,
                          input: option.value === "date_range" ? { start: "", end: "" } : "",
                          active: true,
                        },
                      ];
                      setFilters(newFilters);
                    },
                    style: {
                      cursor: "pointer",
                    },
                  },
                ],
              }))}
          />
        </div>

        <button 
          className="flex items-center space-x-2 rounded-[4px] py-1.5 pl-1.5 pr-3 bg-[#edf1f4] hover:bg-[#bec1c3]"
          onClick={() => setExactMatch(!exactMatch)}
        >
          <input 
            type="checkbox"
            checked={exactMatch}
          />
          <div className="text-[12px] flex flex-col items-end space-y-[-3px]">
            <div>{tr("Exact")}</div>
            <div>{tr("Match")}</div>
          </div>
        </button>
        
        <button onClick={() => setFilters([])} className="text-primary">
          {tr("Clear")}
        </button>
      </div>
      <div className="space-y-2 mx-2">
        {filters.map((filter) => (
          <div key={filter.id} className="flex flex-col border px-2 pt-1 pb-1.5 rounded-md bg-slate-200">
            <div className="flex items-center justify-between mb-1">
              <div>
                {filter.label}:
              </div>
              <div className="flex items-center space-x-2">
                {filter.value === "date_range" && filter.active && (
                  <TooltippedElement
                    message={tr("Range is for the day a plan starts (Start Date)")}
                    placement="top"
                    element={<FaInfoCircle className="text-secondary" />}
                  />
                )}
                <button
                  className="text-secondary hover:text-secondary-dark"
                  onClick={() =>
                    setFilters(
                      filters.map((f) => {
                        if (f.id === filter.id) {
                          return { ...f, active: !f.active };
                        }
                        return f;
                      })
                    )
                  }
                >
                  {filter.active ? (
                    <FaRegEye size={16} />  
                  ) : (
                    <FaRegEyeSlash size={16} />
                  )}
                </button>
                <button
                  className="text-red-500 hover:text-red-600"
                  onClick={() =>
                    setFilters(filters.filter((f) => f.id !== filter.id))
                  }
                >
                  <IoClose size={20} />
                </button>
              </div>
            </div>
            {filter.value === "date_range" ? (
              <>
                <div className="flex space-x-1 items-center mb-1">
                  <span className="text-gray-600">{tr("Start")}</span>
                  <input
                    className={` w-full
                      rounded-sm px-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent
                      ${filter.active ? "bg-white" : "bg-gray-200"}
                    `}
                    type="date"
                    value={
                      typeof filter.input === 'object' && filter.input !== null 
                        ? (filter.input as { start?: string; end?: string }).start || ""
                        : ""
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(
                        filters.map((f) => {
                          if (f.id === filter.id) {
                            const currentInput = typeof f.input === 'object' && f.input !== null 
                              ? f.input as { start?: string; end?: string }
                              : { start: "", end: "" };
                            
                            return { 
                              ...f, 
                              input: { 
                                ...currentInput, 
                                start: newValue 
                              } 
                            };
                          }
                          return f;
                        })
                      );
                    }}
                  />
                  <button 
                    onClick={() => {
                      setFilters(
                        filters.map((f) => {
                          if (f.id === filter.id) {
                            const currentInput = typeof f.input === 'object' && f.input !== null 
                              ? f.input as { start?: string; end?: string }
                              : { start: "", end: "" };
                            
                            return { 
                              ...f, 
                              input: { 
                                start: "", 
                                end: currentInput.end || ""
                              } 
                            };
                          }
                          return f;
                        })
                      );
                    }}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    <IoClose size={16} />
                  </button>
                </div>
                <div className="flex space-x-1 items-center">
                  <span className="text-gray-600 mr-[9px]">{tr("End")}</span>
                  <input
                    className={`w-full
                      rounded-sm px-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent
                      ${filter.active ? "bg-white" : "bg-gray-200"}
                    `}
                    type="date"
                    value={
                      typeof filter.input === 'object' && filter.input !== null 
                        ? (filter.input as { start?: string; end?: string }).end || ""
                        : ""
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(
                        filters.map((f) => {
                          if (f.id === filter.id) {
                            const currentInput = typeof f.input === 'object' && f.input !== null 
                              ? f.input as { start?: string; end?: string }
                              : { start: "", end: "" };
                            
                            return { 
                              ...f, 
                              input: { 
                                ...currentInput, 
                                end: newValue 
                              } 
                            };
                          }
                          return f;
                        })
                      );
                    }}
                  />
                  <button 
                    onClick={() => {
                      setFilters(
                        filters.map((f) => {
                          if (f.id === filter.id) {
                            const currentInput = typeof f.input === 'object' && f.input !== null 
                              ? f.input as { start?: string; end?: string }
                              : { start: "", end: "" };
                            
                            return { 
                              ...f, 
                              input: { 
                                start: currentInput.start || "", 
                                end: "" 
                              } 
                            };
                          }
                          return f;
                        })
                      );
                    }}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    <IoClose size={16} />
                  </button>
                </div>
              </>
            ) : (
              <input
                className={`
                  rounded-sm px-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent
                  ${filter.active ? "bg-white" : "bg-gray-200"}
                `}
                type="text"
                value={typeof filter.input === 'string' ? filter.input : ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFilters(
                    filters.map((f) => {
                      if (f.id === filter.id) {
                        return { ...f, input: newValue };
                      }
                      return f;
                    })
                  );
                }}
              />
            )}

          </div>
        ))}
      </div>
    </div>
  );
}
